import type { ReorderState } from './types.js';

/**
 * Item that participates in a reorder: a row id + its current height
 * along the reorder axis. View-specific geometries translate their
 * domain (uniform row height for Gantt, measured rects for Table) into
 * this list and feed it to {@link computePostDropShifts}.
 */
export interface ReorderShiftItem {
  readonly rowId: string;
  readonly height: number;
}

/**
 * Compute the per-row vertical pixel offset that makes every row appear
 * at its post-drop position while the reorder is live.
 *
 * The algorithm partitions `items` into stationary and dragged rows,
 * re-inserts the dragged block at the clamped insert anchor, and diffs
 * the running top of each row between the current and post-drop order.
 *
 * Works for any view whose rows stack along a single axis — Gantt
 * (uniform heights) and Table (measured heights) share the exact same
 * math, so this helper is the single source of truth.
 */
export function computePostDropShifts(
  items: readonly ReorderShiftItem[],
  state: ReorderState
): Map<string, number> {
  const shifts = new Map<string, number>();
  if (state.targetIndex === null || items.length === 0) return shifts;

  const draggedSet = state.draggedIds;
  const stationary = items.filter(it => !draggedSet.has(it.rowId));
  const draggedOrdered = items.filter(it => draggedSet.has(it.rowId));

  // Clamp the target into the stationary array — dragged rows at or
  // before the target don't consume a slot.
  let stationaryInsertAt = 0;
  for (let i = 0; i < Math.min(state.targetIndex, items.length); i++) {
    if (!draggedSet.has(items[i]!.rowId)) stationaryInsertAt++;
  }
  stationaryInsertAt = Math.max(
    0,
    Math.min(stationaryInsertAt, stationary.length)
  );

  const postOrder: ReorderShiftItem[] = [
    ...stationary.slice(0, stationaryInsertAt),
    ...draggedOrdered,
    ...stationary.slice(stationaryInsertAt),
  ];

  // Running top of each row in the current order — offsets start at 0
  // because shifts are relative; the caller's CSS `top` already holds
  // the absolute position.
  let runningOldTop = 0;
  const oldTopById = new Map<string, number>();
  for (const it of items) {
    oldTopById.set(it.rowId, runningOldTop);
    runningOldTop += it.height;
  }

  let runningNewTop = 0;
  for (const it of postOrder) {
    const oldTop = oldTopById.get(it.rowId) ?? 0;
    const delta = runningNewTop - oldTop;
    if (delta !== 0) shifts.set(it.rowId, delta);
    runningNewTop += it.height;
  }
  return shifts;
}

/**
 * Running-top diff for a scope the dragged rows are LEAVING. Used by
 * multi-scope views (Kanban) for every group that is not the current
 * drop target: stationary rows compact upward to close the gap left by
 * the extracted block, while the dragged rows themselves receive no
 * shift (they follow the pointer via a ghost, or simply stay invisible
 * in the render — the caller decides).
 */
export function computeExtractionShifts(
  items: readonly ReorderShiftItem[],
  draggedIds: ReadonlySet<string>
): Map<string, number> {
  const shifts = new Map<string, number>();
  if (items.length === 0 || draggedIds.size === 0) return shifts;

  let oldTop = 0;
  let newTop = 0;
  for (const it of items) {
    if (draggedIds.has(it.rowId)) {
      oldTop += it.height;
      continue;
    }
    const delta = newTop - oldTop;
    if (delta !== 0) shifts.set(it.rowId, delta);
    oldTop += it.height;
    newTop += it.height;
  }
  return shifts;
}

/**
 * Running-top diff for a scope that is RECEIVING a dragged block whose
 * ids are not currently among {@link items}. Every stationary item at
 * or after {@link targetIndex} shifts down by the combined dragged
 * height to open a slot. Used by multi-scope views (Kanban) for the
 * drop-target group when the source group is a different scope —
 * {@link computePostDropShifts} can't help because its math assumes the
 * dragged rows are already part of the scope's item list.
 */
export function computeInsertionShifts(
  items: readonly ReorderShiftItem[],
  draggedBlockHeight: number,
  targetIndex: number
): Map<string, number> {
  const shifts = new Map<string, number>();
  if (items.length === 0 || draggedBlockHeight === 0) return shifts;
  const clamp = Math.max(0, Math.min(targetIndex, items.length));
  for (let i = clamp; i < items.length; i++) {
    const it = items[i];
    if (it !== undefined) shifts.set(it.rowId, draggedBlockHeight);
  }
  return shifts;
}

/**
 * Dispatches per-scope shift math for multi-scope views (Kanban). Each
 * scope picks the right helper based on whether the dragged set
 * intersects its items:
 *
 * - drop-scope ∩ items ≠ ∅ → intra-scope reorder via
 *   {@link computePostDropShifts}
 * - drop-scope ∩ items = ∅ → cross-scope insertion via
 *   {@link computeInsertionShifts} (needs `draggedBlockHeight`)
 * - other scope with extracted items → {@link computeExtractionShifts}
 * - other scope untouched → no shifts
 */
export function computeGroupedPostDropShifts(
  groups: { readonly [scope: string]: readonly ReorderShiftItem[] },
  state: ReorderState,
  draggedBlockHeight: number
): Map<string, { dx: number; dy: number }> {
  const result = new Map<string, { dx: number; dy: number }>();
  for (const [scope, items] of Object.entries(groups)) {
    const intersects = items.some(it => state.draggedIds.has(it.rowId));
    let perScope: Map<string, number> | null = null;

    if (scope === state.dropScope) {
      if (intersects) {
        perScope = computePostDropShifts(items, state);
      } else if (state.targetIndex !== null) {
        perScope = computeInsertionShifts(
          items,
          draggedBlockHeight,
          state.targetIndex
        );
      }
    } else if (intersects) {
      perScope = computeExtractionShifts(items, state.draggedIds);
    }

    if (perScope) {
      for (const [rowId, dy] of perScope) {
        result.set(rowId, { dx: 0, dy });
      }
    }
  }
  return result;
}
